import { useSyncExternalStore } from 'react';
import { message } from 'antd';
import {
  AGENT_STATUS, SYSTEM_AGENT, callCenterApi, customerByPhone, getCallCenter, isValidPhone,
  type Direction, type Outcome,
} from './callCenterStore';
import { currentUser } from '../Social/socialStore';
import { bindCallEvents, calloutErrorText, cs, isLive } from './caresoft';
import './callLogApi'; // đẩy nhật ký cuộc gọi lên máy chủ khi bật tổng đài thật

/**
 * Trạng thái softphone dùng chung toàn app (giữ cuộc gọi khi chuyển giữa danh sách ↔ chi tiết).
 * idle → calling → connected → wrapup (ghi kết quả) → idle
 * idle → ringing (gọi đến) → connected | nhỡ
 * Khi tổng đài CareSoft đã kết nối (isLive) các bước do sự kiện CareSoft đẩy sang; ngược lại mô phỏng bằng hẹn giờ.
 */
export type Phase = 'idle' | 'ringing' | 'calling' | 'connected' | 'wrapup';

export interface PhoneSession {
  open: boolean;
  phase: Phase;
  direction: Direction;
  phone: string;
  dialer: string;
  startedAt: number;
  connectedAt?: number;
  endedAt?: number;
  /** Kết quả gợi ý khi kết thúc (người dùng có thể đổi ở bước ghi kết quả) */
  outcome?: Outcome;
  callbackId?: string | null;
  /** Mã cuộc gọi bên CareSoft (để tra ghi âm) */
  callId?: string;
  hold?: boolean;
  muted?: boolean;
}

const CONNECT_DELAY = 2000;
const RING_TIMEOUT = 15000;

let session: PhoneSession = { open: false, phase: 'idle', direction: 'out', phone: '', dialer: '', startedAt: 0 };
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

const clearTimer = () => { if (timer) clearTimeout(timer); timer = null; };
const update = (patch: Partial<PhoneSession>) => { session = { ...session, ...patch }; listeners.forEach(l => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export const useSoftphone = () => useSyncExternalStore(subscribe, () => session);
export const getSoftphone = () => session;

export const meName = () => currentUser().name;
export const displayNameOf = (phone: string) => customerByPhone(getCallCenter(), phone)?.name || 'Số máy lạ';
export const talkSeconds = (s: PhoneSession, now = Date.now()) =>
  s.connectedAt ? Math.round(((s.endedAt ?? now) - s.connectedAt) / 1000) : 0;

const IDLE: Partial<PhoneSession> = { phase: 'idle', phone: '', dialer: '', connectedAt: undefined, endedAt: undefined, outcome: undefined, callbackId: null, callId: undefined, hold: false, muted: false };

const logMissedIncoming = (phone: string, caresoftCallId?: string) => {
  callCenterApi.logCall({ phone, direction: 'in', outcome: 'no_answer', duration: 0, agent: SYSTEM_AGENT, caresoftCallId });
  message.warning(`Cuộc gọi nhỡ từ ${displayNameOf(phone)} (${phone})`);
};

export const softphone = {
  openDialer(prefill = '') {
    update({ open: true, ...(session.phase === 'idle' ? { dialer: prefill } : {}) });
  },

  /** Thu nhỏ: cuộc gọi vẫn tiếp tục, nút nổi hiển thị thời gian */
  minimize() { update({ open: false }); },

  setDialer(dialer: string) { update({ dialer }); },

  /** Gọi ra. Trả về false nếu bị chặn (đang có cuộc gọi, agent không sẵn sàng, số sai). */
  dial(phone: string, opts: { callbackId?: string | null } = {}): boolean {
    const num = phone.trim();
    if (session.phase !== 'idle') {
      message.warning('Bạn đang trong một cuộc gọi khác. Kết thúc cuộc gọi hiện tại trước.');
      update({ open: true });
      return false;
    }
    if (!isValidPhone(num)) {
      message.error('Số điện thoại không hợp lệ');
      return false;
    }
    const dnc = customerByPhone(getCallCenter(), num)?.doNotCall;
    if (dnc) {
      message.error(`Số này đang bị chặn gọi (${dnc.reason}). Gỡ chặn trong hồ sơ khách nếu cần gọi.`);
      return false;
    }
    const status = getCallCenter().agentStatus;
    if (status !== 'online') {
      message.warning(`Bạn đang ở trạng thái "${AGENT_STATUS[status].label}". Chuyển sang "Sẵn sàng" để gọi ra.`);
      return false;
    }
    clearTimer();
    update({ ...IDLE, open: true, phase: 'calling', direction: 'out', phone: num, dialer: num, startedAt: Date.now(), callbackId: opts.callbackId ?? null });
    if (isLive()) { cs.callout(num); return true; }
    timer = setTimeout(() => {
      if (session.phase === 'calling') update({ phase: 'connected', connectedAt: Date.now() });
    }, CONNECT_DELAY);
    return true;
  },

  /** Mô phỏng khách gọi vào (demo). Agent không sẵn sàng / đang bận máy → ghi nhận cuộc gọi nhỡ. */
  simulateIncoming(phone?: string) {
    if (isLive()) { message.info('Đang dùng tổng đài thật — không mô phỏng cuộc gọi đến'); return; }
    const s = getCallCenter();
    const pool = [...s.customers.map(c => c.phones[0]), '0398765432', '0861234567'];
    const num = phone || pool[Math.floor(Math.random() * pool.length)];
    if (session.phase !== 'idle' || s.agentStatus !== 'online') {
      logMissedIncoming(num);
      return;
    }
    clearTimer();
    update({ ...IDLE, open: true, phase: 'ringing', direction: 'in', phone: num, startedAt: Date.now() });
    timer = setTimeout(() => {
      if (session.phase === 'ringing') { logMissedIncoming(num); update({ ...IDLE, open: false }); }
    }, RING_TIMEOUT);
  },

  answer() {
    if (session.phase !== 'ringing') return;
    clearTimer();
    if (isLive()) { cs.accept(); return; } // chuyển "connected" khi CareSoft báo csAcceptCall
    update({ phase: 'connected', connectedAt: Date.now() });
  },

  reject() {
    if (session.phase !== 'ringing') return;
    clearTimer();
    if (isLive()) cs.end();
    callCenterApi.logCall({ phone: session.phone, direction: 'in', outcome: 'busy', duration: 0, agent: meName(), caresoftCallId: session.callId });
    update({ ...IDLE, open: false });
  },

  hangup() {
    if (session.phase !== 'calling' && session.phase !== 'connected') return;
    clearTimer(); // huỷ hẹn giờ "kết nối" — tránh cuộc gọi tự nối lại sau khi đã cúp máy
    if (isLive()) cs.end();
    const connected = session.phase === 'connected';
    update({ phase: 'wrapup', endedAt: Date.now(), outcome: connected ? 'answered' : 'no_answer', open: true });
  },

  /** Lưu kết quả cuộc gọi → nhật ký, hẹn gọi lại, tạo khách (nếu số lạ) */
  finish(result: { outcome: Outcome; note?: string; callbackAt?: number | null; newCustomerName?: string }) {
    if (session.phase !== 'wrapup') return;
    const duration = result.outcome === 'answered' ? talkSeconds(session) : 0;
    callCenterApi.logCall({
      phone: session.phone,
      direction: session.direction,
      outcome: result.outcome,
      duration,
      agent: meName(),
      note: result.note,
      startedAt: session.startedAt,
      callbackAt: result.callbackAt,
      callbackId: session.callbackId,
      newCustomerName: result.newCustomerName,
      caresoftCallId: session.callId,
    });
    // Xong cuộc gọi → thu nhỏ softphone để không che màn hình
    update({ ...IDLE, open: false });
  },

  /** Giữ máy / tắt micro — chỉ có khi gọi qua tổng đài thật; trạng thái cập nhật theo sự kiện CareSoft */
  toggleHold() { if (session.phase === 'connected' && isLive()) cs.toggleHold(); },
  toggleMute() { if (session.phase === 'connected' && isLive()) cs.toggleMute(); },
};

// Sự kiện từ tổng đài CareSoft → bước của softphone
bindCallEvents({
  ringing(phone) {
    // CareSoft cũng báo đổ chuông khi đang gọi ra; đang có cuộc khác thì bỏ qua
    if (session.phase !== 'idle') return;
    clearTimer();
    update({ ...IDLE, open: true, phase: 'ringing', direction: 'in', phone, startedAt: Date.now() });
  },
  calloutStarted() { /* đã chuyển "calling" lúc bấm gọi */ },
  agentAccepted() {
    if (session.phase === 'ringing') update({ phase: 'connected', connectedAt: Date.now() });
  },
  customerAccepted() {
    if (session.phase === 'calling') update({ phase: 'connected', connectedAt: Date.now() });
  },
  ended() {
    if (session.phase === 'ringing') { logMissedIncoming(session.phone, session.callId); update({ ...IDLE, open: false }); return; }
    if (session.phase !== 'calling' && session.phase !== 'connected') return;
    const connected = session.phase === 'connected';
    update({ phase: 'wrapup', endedAt: Date.now(), outcome: connected ? 'answered' : 'no_answer', open: true, hold: false, muted: false });
  },
  calloutError(errorCode, sipCode) {
    message.error(calloutErrorText(errorCode, sipCode));
    if (session.phase !== 'calling') return;
    const busy = ['486', '600', '603'].includes(String(sipCode ?? ''));
    update({ phase: 'wrapup', endedAt: Date.now(), outcome: busy ? 'busy' : 'no_answer', open: true });
  },
  callId(callId) { if (session.phase !== 'idle') update({ callId }); },
  hold(on) { update({ hold: on }); },
  mute(on) { update({ muted: on }); },
});

/** Chỉ dùng trong test */
export function __resetSoftphone() {
  clearTimer();
  session = { open: false, phase: 'idle', direction: 'out', phone: '', dialer: '', startedAt: 0 };
}
